import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';

export const AUTOMATICK_THEMES = [
  'automatick-green',
  'automatick-dark',
] as const;

export type AutomatickTheme = (typeof AUTOMATICK_THEMES)[number];

export const AUTOMATICK_DEFAULT_THEME: AutomatickTheme = 'automatick-green';
export const AUTOMATICK_THEME_STORAGE_KEY =
  'automatick.theme.preference.v1';

export function isAutomatickTheme(value: unknown): value is AutomatickTheme {
  return (
    typeof value === 'string' &&
    AUTOMATICK_THEMES.some((theme) => theme === value)
  );
}

@Injectable({ providedIn: 'root' })
export class AutomatickThemePreferenceService {
  private readonly document = inject(DOCUMENT);
  private readonly selectedThemeState = signal<AutomatickTheme>(
    AUTOMATICK_DEFAULT_THEME,
  );

  readonly selectedTheme = this.selectedThemeState.asReadonly();

  initialize(): AutomatickTheme {
    const selectedTheme =
      this.readStoredTheme() ?? AUTOMATICK_DEFAULT_THEME;
    this.applyTheme(selectedTheme);
    return selectedTheme;
  }

  selectTheme(theme: AutomatickTheme): void {
    this.applyTheme(theme);
    this.writeStoredTheme(theme);
  }

  private applyTheme(theme: AutomatickTheme): void {
    this.document.documentElement.setAttribute('data-theme', theme);
    this.document.body?.setAttribute('data-theme', theme);
    this.selectedThemeState.set(theme);
  }

  private readStoredTheme(): AutomatickTheme | null {
    try {
      const value = this.document.defaultView?.localStorage.getItem(
        AUTOMATICK_THEME_STORAGE_KEY,
      );
      return isAutomatickTheme(value) ? value : null;
    } catch {
      return null;
    }
  }

  private writeStoredTheme(theme: AutomatickTheme): void {
    try {
      this.document.defaultView?.localStorage.setItem(
        AUTOMATICK_THEME_STORAGE_KEY,
        theme,
      );
    } catch {
      // Storage can be unavailable in hardened browser or extension contexts.
    }
  }
}
